import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Classification, ClassificationGroup } from '@core/generated/gql-types';

interface SkillSetSelectorProps {
  value: Classification;
  onChange: (value: Classification) => void;
  classificationGroups: ClassificationGroup[];
  isLoading: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  error?: string;
  touched?: boolean;
}

const SkillSetSelector: React.FC<SkillSetSelectorProps> = ({
  value,
  onChange,
  classificationGroups,
  isLoading,
  searchValue,
  onSearchChange,
  error,
  touched,
}) => {
  const filteredGroups = classificationGroups
    .map((group) => ({
      ...group,
      classifications:
        group.classifications?.filter((c) =>
          c?.name?.toLowerCase().includes(searchValue.toLowerCase()),
        ) || [],
    }))
    .filter((group) => group.classifications.length > 0);

  return (
    <div>
      <label className="mb-1 block text-sm font-medium">Select skill set</label>
      <Listbox value={value} onChange={onChange} disabled={isLoading}>
        <div className="relative">
          <Listbox.Button className="relative w-full cursor-default rounded-lg border bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:outline-hidden focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-300 disabled:bg-gray-50 disabled:text-gray-500">
            <span className="block truncate">
              {filteredGroups
                .find((g) => g.id === value?.classificationGroupId)
                ?.classifications.find((c) => c.id === value?.id)?.name ||
                'Select skill set'}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Transition
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
              <div className="px-3 py-2">
                <input
                  type="text"
                  className="w-full rounded-sm border px-2 py-1 text-sm"
                  placeholder="Search job title..."
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </div>
              {filteredGroups.map((group) => (
                <React.Fragment key={group.id}>
                  <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500">
                    {group.name}
                  </div>
                  {[...(group.classifications || [])]
                    .sort((a, b) =>
                      (a?.name || '').localeCompare(b?.name || ''),
                    )
                    .map((classification) => (
                      <Listbox.Option
                        key={classification.id}
                        value={classification}
                        className={({ active }) =>
                          `relative cursor-default px-4 py-2 select-none ${
                            active
                              ? 'bg-blue-100 text-blue-900'
                              : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <div className="flex items-center gap-2">
                            <span
                              className={`min-w-0 flex-1 truncate ${
                                selected ? 'font-medium' : 'font-normal'
                              }`}
                            >
                              {classification.name}
                            </span>
                            {selected ? (
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="h-4 w-4 shrink-0"
                              />
                            ) : null}
                          </div>
                        )}
                      </Listbox.Option>
                    ))}
                </React.Fragment>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      {touched && error && (
        <div className="mt-1 text-xs text-red-500">{error}</div>
      )}
    </div>
  );
};

export default SkillSetSelector;
